// The product's delivery methods (StoreProduct.shippingMethods): the company's
// — every provider and method it ships with — or a choice of them made for
// this product alone (one that cannot go to a locker). The company's providers
// come with the product (`product {raw}` companyShipping, read-only); a
// method the company has off is not offered here at all, and checkout never
// offers it whatever the product says.
import React from "react"
import Typography from "aldoo-ra/Typography"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { TruckIcon, BuildingStorefrontIcon, ArchiveBoxIcon, ArrowRightIcon } from "@heroicons/react/24/outline"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import { Empty, Select } from "../../../AdminShared/ui"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const METHOD_ICONS = { address: TruckIcon, office: BuildingStorefrontIcon, locker: ArchiveBoxIcon }

/** The company's providers that are on, each with only its methods that are on; a provider with none left out. */
export const companyMethods = (companyShipping) =>
  (companyShipping?.providers || [])
    .filter((provider) => provider.enabled !== false)
    .map((provider) => ({ ...provider, methods: (provider.methods || []).filter((method) => method.enabled !== false) }))
    .filter((provider) => provider.methods.length)

const entryOf = (value, providerId) => (value?.providers || []).find((entry) => entry?.providerId === providerId) || null
const providerOn = (value, providerId) => {
  const entry = entryOf(value, providerId)
  return !!entry && entry.enabled !== false
}
const methodOn = (value, providerId, methodId) => providerOn(value, providerId) && entryOf(value, providerId)?.methods?.[methodId]?.enabled === true

/** Whether a product's own choice leaves it at least one of the company's methods (always, in "company" mode). */
export const productMethodsValid = (value, companyShipping) => {
  if (value?.mode !== "custom") return true
  return companyMethods(companyShipping).some((provider) => provider.methods.some((method) => methodOn(value, provider.providerId, method.id)))
}

/** Whether a content type ships: an unknown one is given the benefit of the doubt. */
export const shipsPhysically = (contentType) => !contentType || !!contentType.isPhysical || contentType.kind === "physical"

// the portal's delivery settings, its couriers' tab, of the product's company
const portalDeliveryUrl = (orgId) => {
  const prefix = typeof window !== "undefined" && /^\/[a-z]{2}(\/|$)/.test(window.location.pathname) ? window.location.pathname.slice(0, 3) : ""
  return `${prefix}/vendor?org=${encodeURIComponent(orgId)}&section=shipping&delivery=providers`
}

function MethodCard({ method, on, editable, onChange }) {
  const t = useProductEditorTexts()
  const Icon = METHOD_ICONS[method.id] || TruckIcon
  return (
    <div className={`flex flex-col gap-1.5 rounded-lg border p-3 ${on ? "border-admin_primary/40 bg-admin_primary/5" : "border-admin_bordered"}`} data-testid={`product-shipping-method-${method.id}`}>
      <div className="flex items-center gap-2">
        {editable && (
          <span>
            <ToggleSwitch variant="admin" isOn={on} onChange={(enabled) => onChange(!!enabled)} />
          </span>
        )}
        <Icon className={`w-5 h-5 shrink-0 ${on ? "text-admin_primary" : "text-admin_text_secondary"}`} />
        <span className="flex-1 text-sm font-semibold text-admin_text">{t(method.label)}</span>
      </div>
      {method.detail && <span className="text-xs text-admin_text_secondary">{t(method.detail)}</span>}
      {method.maxWeightGrams && <span className="text-xs text-admin_text_secondary">{t("Up to %s kg per parcel", method.maxWeightGrams / 1000)}</span>}
    </div>
  )
}

export default function DeliveryMethods({ editor, isProductEditor }) {
  const t = useProductEditorTexts()
  const { read, set } = editor
  const companyShipping = read("companyShipping")
  const value = read("shippingMethods") || { mode: "company", providers: [] }
  const mode = value.mode === "custom" ? "custom" : "company"
  const providers = companyMethods(companyShipping)
  const orgId = companyShipping?.orgId || (typeof editor.product?.publisher === "string" ? editor.product.publisher : editor.product?.publisher?._id)
  const editable = !!isProductEditor && mode === "custom"

  const save = (providersList, nextMode = mode) => set("shippingMethods", { mode: nextMode, providers: providersList })
  // the product's entry of a provider, created as the company's (every method on) when it has none yet
  const withEntry = (providerId, patch) => {
    const list = value.providers || []
    const existing = entryOf(value, providerId)
    const provider = providers.find((item) => item.providerId === providerId)
    const base = existing || { providerId, enabled: true, methods: Object.fromEntries((provider?.methods || []).map((method) => [method.id, { enabled: true }])) }
    const next = patch(base)
    return existing ? list.map((entry) => (entry.providerId === providerId ? next : entry)) : [...list, next]
  }
  const setMode = (next) => {
    if (next === mode) return
    // the first custom choice starts from the company's: every way it ships, on
    if (next === "custom" && !(value.providers || []).length) {
      return save(providers.map((provider) => ({ providerId: provider.providerId, enabled: true, methods: Object.fromEntries(provider.methods.map((method) => [method.id, { enabled: true }])) })), "custom")
    }
    save(value.providers || [], next)
  }
  const setProvider = (providerId, enabled) => save(withEntry(providerId, (entry) => ({ ...entry, enabled })))
  const setMethod = (providerId, methodId, enabled) => save(withEntry(providerId, (entry) => ({ ...entry, enabled: entry.enabled !== false || enabled, methods: { ...(entry.methods || {}), [methodId]: { enabled } } })))
  const invalid = !productMethodsValid(value, companyShipping)

  return (
    <div className="flex flex-col gap-3 pt-2 border-t border-admin_bordered" data-testid="product-delivery-methods">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <Typography component="h4" variant="admin_h4" className="my-0 mb-1 inline-flex items-center gap-1.5">{t`Delivery methods`}<HelpHint helpKey="vendor.product.setup#delivery-methods" /></Typography>
          <p className="text-sm text-admin_text_secondary my-0">{t`The ways a buyer can receive this product. Checkout offers a parcel only the ways every product in it allows.`}</p>
        </div>
        {orgId && (
          <a href={portalDeliveryUrl(orgId)} className="inline-flex items-center gap-1 text-sm font-semibold text-admin_primary hover:underline" data-testid="product-delivery-company-link">
            {t`Change the company's delivery settings`}
            <ArrowRightIcon className="w-4 h-4" />
          </a>
        )}
      </div>
      {/* the choice as a dropdown: each option its title and what it means, the closed field its title */}
      <Select
        value={mode}
        disabled={!isProductEditor}
        onChange={(next) => next && setMode(next)}
        options={[
          { value: "company", label: t`Same as the company`, description: t`Every delivery method the company ships with.` },
          { value: "custom", label: t`Custom selection`, description: t`Only the methods chosen here, among the company's.` },
        ]}
        onItemRender={(option) => (
          <span className="flex flex-col py-0.5">
            <span className="text-sm font-medium text-admin_text">{option.label}</span>
            <span className="text-xs text-admin_text_secondary">{option.description}</span>
          </span>
        )}
        onValueItemRender={(option) => <span className="truncate">{option.label}</span>}
        className="w-full max-w-[420px]"
        data-testid="product-delivery-mode"
      />
      {!providers.length && <Empty>{t`The company has no delivery method on: physical products cannot be delivered.`}</Empty>}
      {invalid && (
        <p className="m-0 text-xs text-admin_error" role="alert" data-testid="product-delivery-methods-error">
          {t`Choose at least one delivery method for this product.`}
        </p>
      )}
      {providers.length > 0 && (
        <div className="rounded-xl border border-admin_bordered bg-admin_paper divide-y divide-admin_bordered">
          {providers.map((provider) => {
            const on = mode === "company" || providerOn(value, provider.providerId)
            return (
              <div key={provider.providerId} className="flex flex-col gap-3 px-4 py-3" data-testid={`product-shipping-provider-${provider.providerId}`}>
                <div className="flex items-center gap-3">
                  {editable && (
                    <span>
                      <ToggleSwitch variant="admin" isOn={on} onChange={(enabled) => setProvider(provider.providerId, !!enabled)} />
                    </span>
                  )}
                  <TruckIcon className={`w-6 h-6 shrink-0 ${on ? "text-admin_primary" : "text-admin_text_secondary"}`} />
                  <span className={`flex-1 font-semibold text-admin_text ${on ? "" : "opacity-60"}`}>{provider.name}</span>
                  {!on && <span className="text-xs text-admin_text_secondary">{t`Off`}</span>}
                </div>
                <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 ${on ? "" : "opacity-50"}`}>
                  {provider.methods.map((method) => (
                    <MethodCard
                      key={method.id}
                      method={method}
                      on={mode === "company" || methodOn(value, provider.providerId, method.id)}
                      editable={editable && on}
                      onChange={(enabled) => setMethod(provider.providerId, method.id, enabled)}
                    />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
